import { VastError } from './VastError';

export const HLS_TYPES  = new Set(['application/x-mpegurl', 'application/vnd.apple.mpegurl']);
export const DASH_TYPES = new Set(['application/dash+xml']);
export const MP4_TYPES  = new Set(['video/mp4', 'video/webm', 'video/ogg']);

/**
 * Selects the best MediaFile and attaches hls.js / dash.js / native src.
 * Priority: HLS → DASH → MP4 (highest bitrate first).
 * HLS fatal errors automatically retry the best available MP4.
 */
export class MediaSourceHandler {
  static select(files) {
    const hls  = files.filter(m => HLS_TYPES.has(m.type));
    const dash = files.filter(m => DASH_TYPES.has(m.type));
    const mp4  = files.filter(m => MP4_TYPES.has(m.type));
    const Hls = window.Hls;
    const hlsOk  = hls.length  && (Hls?.isSupported() ||
      document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '');
    const dashOk = dash.length && !!window.dashjs;
    if (hlsOk)  return { file: hls[0],  format: 'hls' };
    if (dashOk) return { file: dash[0], format: 'dash' };
    if (mp4.length)
      return { file: mp4.slice().sort((a,b) => b.bitrate - a.bitrate)[0], format: 'mp4' };
    if (files.length) return { file: files[0], format: 'auto' };
    throw new VastError(403, 'No supported media file');
  }

  static attach(videoEl, file, format, allFiles = [], onFatalError = null) {
    this.detach(videoEl);
    if (format === 'hls') {
      const Hls = window.Hls;
      if (Hls?.isSupported()) {
        const hls = new Hls({ enableWorker: true, startLevel: -1 });
        hls.loadSource(file.url);
        hls.attachMedia(videoEl);
        videoEl._vastHls = hls;

        // ── Bitrate logging ───────────────────────────────────────────────────
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const levels = hls.levels;
          console.log('[ABR] Manifest parsed. Available levels:', levels.map((l, i) => ({
            level: i,
            bitrate: `${(l.bitrate / 1000).toFixed(0)} kbps`,
            resolution: `${l.width}x${l.height}`,
            codec: l.codecs
          })));
        });

        hls.on(Hls.Events.LEVEL_SWITCHING, (_, data) => {
          const level = hls.levels[data.level];
          console.log('[ABR] Switching to level', data.level, '—', {
            bitrate: `${(level.bitrate / 1000).toFixed(0)} kbps`,
            resolution: `${level.width}x${level.height}`
          });
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          const level = hls.levels[data.level];
          console.log('[ABR] Switched to level', data.level, '✓', {
            bitrate: `${(level.bitrate / 1000).toFixed(0)} kbps`,
            resolution: `${level.width}x${level.height}`
          });
        });

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (!data.fatal) return;
          console.warn('[VOSP] hls.js fatal:', data.type, '— trying MP4 fallback');
          this.detach(videoEl);
          const mp4 = allFiles.filter(m => MP4_TYPES.has(m.type))
            .sort((a,b) => b.bitrate - a.bitrate)[0];
          if (mp4) { videoEl.src = mp4.url; videoEl.load(); }
          else onFatalError?.();
        });
      } else {
        videoEl.src = file.url; // Safari native HLS
      }
    } else if (format === 'dash') {
      const p = window.dashjs.MediaPlayer().create();
      p.initialize(videoEl, file.url, false);
      videoEl._vastDash = p;
    } else {
      videoEl.src = file.url;
    }
  }

  static detach(videoEl) {
    if (videoEl._vastHls)  { videoEl._vastHls.destroy();  delete videoEl._vastHls; }
    if (videoEl._vastDash) { videoEl._vastDash.reset();   delete videoEl._vastDash; }
    videoEl.srcObject = null;
    videoEl.removeAttribute('src');
    videoEl.load();
  }
}